<template>
  <container class="leads">
    <v-card-title>
      <v-row>
        <v-col cols="12" sm="6" md="8">{{
          $capitalize($tc("model.leads_leads_title"))
        }}</v-col>
        <v-col class="text-right">
          <v-text-field
            v-model="filter"
            :label="$capitalize($tc('model.leads_search_leads'))"
            prepend-icon="mdi-magnify"
            @keyup.enter="getLeads"
            :disabled="loading"
            clearable
            single-line
            v-if="filter !== null"
          ></v-text-field>
          <v-btn v-else icon @click="filter = ''">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="getLeads()">
                <v-list-item-title>
                  <v-icon left>mdi-refresh</v-icon
                  >{{ $capitalize($tc("model.leads_update_button")) }}
                </v-list-item-title>
              </v-list-item>
              <ScopeProvider scope="leads.import_lead">
                <v-list-item @click="eps_import_dialog = true">
                  <v-list-item-title>
                    <v-icon left>mdi-import</v-icon
                    >{{ $capitalize($tc("model.leads_eps_import_button")) }}
                  </v-list-item-title>
                </v-list-item>
              </ScopeProvider>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card>
      <v-data-table
        :headers="headers"
        :items="items"
        :items-per-page="5"
        :disabled="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        :loading-text="$capitalize($tc('model.leads_table_loading'))"
        fixed-header
        max-height="500px"
      >
        <template v-slot:top>
          <div class="pa-4">
            <LeadFilter
              v-model="advanced_filter"
              @input="handleFilterChange"
              @fetch="getLeads"
            />
          </div>
        </template>
        <template v-slot:header.identifier_code>
          {{ $capitalize($tc("model.leads_identifier_code_lead", 1)) }}
        </template>

        <template v-slot:header.managers>
            {{ $capitalize($tc("model.teams_managers_table_column", 1)) }}
        </template>

        <template v-slot:header.team_name>
          {{ $capitalize($tc("model.teams_team_table_column", 1)) }}
        </template>

        <template v-slot:header.current_status_id>
          {{ $capitalize($tc("model.leads_status_lead", 1)) }}
        </template>

        <template v-slot:header.insurer_name>
          {{ $capitalize($tc("model.leads_company_name", 1)) }}
        </template>

        <template v-slot:header.lead_type_description>
          {{ $capitalize($tc("model.leads_lead_type", 1)) }}
        </template>

        <template v-slot:header.insured_name>
          {{ $capitalize($tc("model.leads_insured_for_lead", 1)) }}
        </template>

        <template v-slot:header.actions>
          {{ $capitalize($tc("model.leads_actions_button", 1)) }}
        </template>

        <!-- <template v-slot:item.service_manager_avatar="{ item }">
          <v-avatar color="primary" size="30">
            <img
              :src="item.service_manager_avatar"
              :alt="item.name"
              v-if="item.service_manager_avatar"
            />
            <v-icon v-else class="white--text">mdi-account-circle</v-icon>
          </v-avatar>
        </template> -->

        <template v-slot:item.managers="{ item }">
          <user-avatar
            :user_name="item.service_manager_name"
            :avatar_url="item.service_manager_avatar"
            :user_id="item.service_manager_user_id"
            size="35"
          />
        </template>

        <template v-slot:item.insurer_name="{ item }">
          <span :title="item.insurer_name" class="d-inline-block text-truncate">
            {{ item.insurer_name }}
          </span>
        </template>

        <template v-slot:item.current_status_id="{ item }">
          <ScopeProvider scope="leads.update_status">
            <v-select
              :items="lead_statuses"
              label="Standard"
              dense
              single-line
              hide-details
              solo
              flat
              item-text="name"
              item-value="id"
              :value="item.status"
              @change="changeLeadStatus(item, $event)"
              return-object
              :loading="loading_status == item.id"
              :disabled="loading_status == item.id"
              :readonly="
                !$store.getters['auth/getScope']('leads.update_status')
              "
            >
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon small :color="item.color" left
                        >mdi-checkbox-blank-circle</v-icon
                      >
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{
                      item.description
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip
                  label
                  value
                  small
                  :color="item.color == 'yellow' ? 'orange' : item.color"
                  dark
                  >{{ item.name }}</v-chip
                >
              </template>
            </v-select>
            <template v-slot:not>
              <v-chip
                label
                value
                small
                :color="
                  item.status.color == 'yellow' ? 'orange' : item.status.color
                "
                dark
                >{{ item.status.name }}</v-chip
              >
            </template>
          </ScopeProvider>
        </template>
        <template v-slot:item.lead_type_description="{ item }">
          <ScopeProvider scope="leads.update_type">
            <select-lead-types
              v-model="item.lead_type_id"
              :lead_id="item.id"
              label="Tipos"
              solo
              flat
            ></select-lead-types>
            <template v-slot:not>
              <span>{{ item.lead_type.description }}</span>
            </template>
          </ScopeProvider>
        </template>

        <template v-slot:item.external_status="{ item }">
          <v-chip label value>{{ item.external_status }}</v-chip>
        </template>

        <template v-slot:item.claim_complexity="{ item }">
          <span v-if="item.claim_complexity">{{ item.claim_complexity }}</span>
          <span v-else class="grey--text">Não informado</span>
        </template>

        <template v-slot:item.insured_name="{ item }">
          <v-chip @click="handleClickEditContact(item.insured_id)" small>
            <v-avatar left>
              <v-icon>mdi-account-circle</v-icon>
            </v-avatar>
            <span class="d-inline-block text-truncate" style="max-width: 190px">
              {{ item.insured_name }}
            </span>
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon>
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item :to="`/leads/${item.identifier_code}`">
                <v-list-item-title>
                  <v-icon left>mdi-pencil</v-icon
                  >{{ $capitalize($tc("model.leads_details_lead_button")) }}
                </v-list-item-title>
              </v-list-item>
              <ScopeProvider scope="leads.approve">
                <div v-if="item.approved == null">
                  <v-divider></v-divider>
                  <v-subheader>{{
                    $capitalize($tc("model.leads_rating_subheader"))
                  }}</v-subheader>
                  <v-list-item @click="acceptLead(item)">
                    <v-list-item-title class="green--text">
                      <v-icon left color="green">mdi-check</v-icon
                      >{{ $capitalize($tc("model.leads_accept_lead_button")) }}
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="confirmRefuseLead(item)">
                    <v-list-item-title class="red--text">
                      <v-icon left color="red">mdi-close</v-icon
                      >{{ $capitalize($tc("model.leads_refuse_lead_button")) }}
                    </v-list-item-title>
                  </v-list-item>
                </div>
              </ScopeProvider>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="contact_dialog" max-width="600px">
      <contact-form
        @cancel="handleCancelDialog"
        @submit="handleSubmitDialog"
        :id="contact_id"
      ></contact-form>
    </v-dialog>

    <v-dialog v-model="confirmation_modal" max-width="600px">
      <v-card>
        <v-form @submit.prevent="refuseLead(confirmation_action)">
          <ValidationObserver ref="confirmation_modal" v-slot="{ invalid }">
            <v-card-title>
              {{ $capitalize($tc("model.leads_confirmation_modal")) }}
              <v-spacer></v-spacer>
              <v-btn @click="confirmation_modal = false" icon>
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              {{ $capitalize($tc("model.leads_message_reprove_lead")) }}
              <ValidationProvider
                name="approve_justification"
                rules="required"
                v-slot="{ errors }"
              >
                <v-textarea
                  v-model="approve_justification"
                  name="approve_justification"
                  :label="$capitalize($tc('model.leads_label_justification'))"
                  filled
                  :error-messages="errors"
                ></v-textarea>
              </ValidationProvider>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn @click="confirmation_modal = false" text>{{
                $capitalize($tc("model.leads_cancel_button"))
              }}</v-btn>
              <v-btn type="submit" text color="red" :disabled="invalid">{{
                $capitalize($tc("model.leads_refuse_lead_button"))
              }}</v-btn>
            </v-card-actions>
          </ValidationObserver>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="eps_import_dialog" max-width="400px">
      <eps-import-lead-form
        @cancel="eps_import_dialog = false"
        @submitted="handle_eps_import_dialog_submit"
      ></eps-import-lead-form>
    </v-dialog>
  </container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { changeStatus, approveLead, change_manager } from "@/services/lead";

import ScopeProvider from "@/components/ScopeProvider";
import ContactForm from "@/components/forms/ContactForm";
import EpsImportLeadForm from "@/components/EPS/EpsImportLeadForm";
import SelectLeadTypes from "@/components/SelectLeadTypes";
import UserAvatar from "@/components/UserAvatar";
import LeadFilter from "@/components/LeadFilter";

export default {
  components: {
    ContactForm,
    SelectLeadTypes,
    EpsImportLeadForm,
    ScopeProvider,
    UserAvatar,
    LeadFilter,
  },
  data: () => ({
    advanced_filter: {
      managers: [],
      statuses: [],
      types: [],
      insureds: [],
      insurers: [],
    },
    filter: null,
    loading: false,
    items: [],
    contact_id: null,
    contact_dialog: false,
    headers: [
      {
        text: "Identificação",
        value: "identifier_code",
      },
      {
        text: "Equipe",
        value: "team_name",
        width: 140,
      },
      {
        text: "Gestor",
        value: "managers",
        align: "center",
        sortable: false,
        width: 150,
      },
      {
        text: "Status",
        value: "current_status_id",
        width: 200,
      },
      {
        text: "Companhia",
        value: "insurer_name",
      },
      {
        text: "Tipo",
        value: "lead_type_description",
        width: 250,
        // TODO: Corrigir ordenação da relação
        sortable: false,
      },
      {
        text: "Segurado",
        value: "insured_name",
        align: "left",
      },
      {
        text: "Acções",
        value: "actions",
        sortable: false,
      },
    ],
    options: {
      itemsPerPage: 10,
      sortBy: ["creation_date"],
      sortDesc: [],
    },
    totalItems: 0,
    loading_status: null,
    confirmation_modal: false,
    confirmation_action: null,
    approve_justification: "",
    eps_import_dialog: false,
  }),
  methods: {
    async handleManagerChange(lead, service_manager) {
      lead.loading_avatar = true;
      try {
        await change_manager(lead.id, { service_manager });
        lead.service_manager_avatar = service_manager.user_avatar;
        lead.service_manager_name = service_manager.user_name;
        lead.service_manager_user_id = service_manager.user_id;
      } catch (e) {
        this.$store.commit("sendMessage", {
          text: "Falha ao modificar gestor",
          color: "red",
        });
      } finally {
        lead.loading_avatar = false;
      }
    },
    async handle_eps_import_dialog_submit() {
      this.eps_import_dialog = false;
      this.getLeads();
    },
    async changeLeadStatus(lead, status) {
      this.loading_status = lead.id;
      try {
        const new_status = await changeStatus(lead.id, status.id);
        lead.status = new_status.status;
      } catch (error) {
        console.error(error);
        alert("error");
      } finally {
        if (this.loading_status === lead.id) this.loading_status = null;
      }
    },
    lastStatusForHumans(updated_at) {
      const attempt_moment = this.$moment(updated_at);
      const now = this.$moment();

      return this.$moment.duration(attempt_moment.diff(now)).humanize(true);
    },
    async getLeads() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      this.loading = true;
      try {
        this.items = [];

        const response = await this.$http.get("/leads", {
          params: {
            orderBy: sortBy[0] ? sortBy[0] : "",
            sortDesc: sortDesc[0] ? sortBy[0] : "",
            page,
            perPage: itemsPerPage,
            filter: this.filter || "",
            ...this.parsedQueryFilters(),
          },
        });
        this.items = response.data.data;
        this.totalItems = response.data.total;
        this.advanced_filter = {
          ...this.advanced_filter,
          ...response.data.metadata.advanced_filter,
        };
      } catch (e) {
        this.advanced_filter = {
          managers: [],
          statuses: [],
          types: [],
          insureds: [],
          insurers: [],
        };
      } finally {
        this.loading = false;
      }
    },
    handleClickEditContact(id) {
      this.contact_id = id;
      this.contact_dialog = true;
    },
    handleSubmitDialog() {
      this.contact_id = null;
      this.contact_dialog = false;
      this.getLeads();
    },
    handleCancelDialog() {
      this.contact_id = null;
      this.contact_dialog = false;
    },
    async acceptLead(lead) {
      this.$store.commit("setLoading", { loading: true });
      try {
        const response = await approveLead(lead.id, true);
        lead.status = response.status;
        lead.approved = response.approved;

        this.$store.commit("sendMessage", {
          text: "Lead aprovada com sucesso",
        });
      } catch (error) {
      } finally {
        this.$store.commit("setLoading", { loading: false });
      }
    },
    async refuseLead(lead) {
      this.$store.commit("setLoading", { loading: true });
      try {
        const response = await approveLead(
          lead.id,
          false,
          this.approve_justification
        );
        lead.status = response.status;
        lead.approved = response.approved;

        this.confirmation_modal = false;

        this.$store.commit("sendMessage", {
          text: "Lead recusada com sucesso",
        });
      } catch (error) {
        if (error.response.status == 422) {
          this.$refs.confirmation_modal.setErrors(error.response.data.errors);
          this.$store.commit("sendMessage", {
            text: error.response.data.message,
            color: "red",
          });
        } else {
          this.$store.commit("sendMessage", {
            text: "Falha ao reprovar lead",
            color: "red",
          });
        }
      } finally {
        this.$store.commit("setLoading", { loading: false });
      }
    },
    confirmRefuseLead(lead) {
      if (this.$refs.confirmation_modal) {
        this.$refs.confirmation_modal.reset();
      }

      this.approve_justification = "";
      this.confirmation_modal = true;
      this.confirmation_action = lead;
    },
    handleFilterChange(advanced_filter) {
      this.setQueryFilter(advanced_filter);
      localStorage.setItem(
        "leads_advanced_filters",
        JSON.stringify(advanced_filter)
      );
    },
    getAdvancedFiltersFromStorage() {
      if (!this.hasFilter()) {
        const json_storage_filters = localStorage.getItem(
          "leads_advanced_filters"
        );
        if (json_storage_filters) {
          const parsed_storage_filters = JSON.parse(json_storage_filters);
          this.setQueryFilter(parsed_storage_filters);
        }
      }
    },
    setQueryFilter(advanced_filter) {
      this.$router.push({
        path: "leads",
        query: {
          managers:
            advanced_filter.managers.length > 0
              ? JSON.stringify(
                  advanced_filter.managers.map((manager) => manager.id)
                )
              : undefined,
          statuses:
            advanced_filter.statuses.length > 0
              ? JSON.stringify(
                  advanced_filter.statuses.map((status) => status.id)
                )
              : undefined,
          types:
            advanced_filter.types.length > 0
              ? JSON.stringify(advanced_filter.types.map((type) => type.id))
              : undefined,
          insurers:
            advanced_filter.insurers.length > 0
              ? JSON.stringify(
                  advanced_filter.insurers.map((insurer) => insurer.id)
                )
              : undefined,
          insureds:
            advanced_filter.insureds.length > 0
              ? JSON.stringify(
                  advanced_filter.insureds.map((insured) => insured.id)
                )
              : undefined,
        },
      });
    },
    parsedQueryFilters() {
      return {
        managers: this.$route.query.managers
          ? JSON.parse(this.$route.query.managers)
          : [],
        statuses: this.$route.query.statuses
          ? JSON.parse(this.$route.query.statuses)
          : [],
        types: this.$route.query.types
          ? JSON.parse(this.$route.query.types)
          : [],
        insurers: this.$route.query.insurers
          ? JSON.parse(this.$route.query.insurers)
          : [],
        insureds: this.$route.query.insureds
          ? JSON.parse(this.$route.query.insureds)
          : [],
      };
    },
    hasFilter() {
      return (
        this.$route.query.managers ||
        this.$route.query.statuses ||
        this.$route.query.types ||
        this.$route.query.insurers ||
        this.$route.query.insureds
      );
    },
    ...mapActions("lead_statuses", ["loadStatuses"]),
    ...mapActions("lead_types", ["loadTypes"]),
  },
  watch: {
    options: {
      handler() {
        this.getAdvancedFiltersFromStorage();
        this.getLeads();
      },
      deep: true,
    },
    filter: function (value) {
      if (value == null) this.getLeads();
    },
  },
  computed: {
    ...mapState("lead_statuses", ["lead_statuses"]),
  },
  created() {
    this.loadStatuses();
    this.loadTypes();
  },
};
</script>
